/**
 * THE WORK-TYPE BADGE — one component, because the distinction must be identical
 * everywhere it is drawn (Projects design §5.1, build plan C8).
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ⚠ A BADGE, NOT A COLOUR ALONE, AND THE THREE CHANNELS ARE THE ARGUMENT
 * ═══════════════════════════════════════════════════════════════════════════════
 * "Which of these rows are projects" has to be answerable from a SCREENSHOT pasted
 * into a chat, by a COLOUR-BLIND reader, and from a CSV EXPORT opened in Excel. A
 * tint reaches none of those reliably and the third not at all. So the badge carries
 * the WORD, and the tint is the redundant half — present for scanning speed, never
 * load-bearing. `WORK_TYPE_LABEL` is the same map the CSV writes.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ⚠ WHY THE `crf` BADGE IS OPTIONAL AND THE `project` BADGE IS NOT
 * ═══════════════════════════════════════════════════════════════════════════════
 * Every record in this system was a CRF until migration 37, and the overwhelming
 * majority still are. Painting "CRF" on several hundred rows to mark the two that
 * are not is noise that trains the reader to ignore the column — the failure mode a
 * badge exists to avoid. So `only="project"` draws the badge on projects and nothing
 * on CRFs, which is the right default for a register whose universe is mixed.
 *
 * ⚠ BUT IT IS NOT THE DEFAULT ON A SCREEN WHOSE UNIVERSE IS DELIBERATELY NARROWED.
 * When the operator has asked for "CRFs" or "Projects" explicitly, or is looking at
 * ONE record, the badge states which universe they are in — because an absent badge
 * and "this build does not know" look identical, and on a single record there is no
 * neighbouring row to compare against. Those callers pass `only={null}`.
 *
 * ⚠ A `null` WORK TYPE IS NOT A CRF. `work_orders.work_type` is `LEFT JOIN`ed off
 * `crfs`, so a work order with no CRF reports null; the API types it `WorkType | null`
 * rather than defaulting. Defaulting it here would print "CRF" on a row nobody has
 * classified — a claim, from a component that has no way to know. Null renders
 * nothing.
 */

import { cn } from '../../lib/cn';
import { WORK_TYPE_LABEL } from './domain';
import type { WorkType } from './domain';

/**
 * The tint pairs. NO NEW COLOUR IS INVENTED: both pairs already exist in
 * `tokens.css` and are already carried by the AA contrast suites — `bg-subtle` +
 * `text-fg` + `ring-line` is the neutral recipe every fallback pill in this app
 * uses, and `bg-info-bg` + `text-info-fg` is the informational pair the CRF stage
 * pill and the renewals `upcoming` pill both already draw.
 *
 * A project is INFORMATIONAL, not a warning: it is a normal kind of work, and
 * painting it amber would send somebody to investigate a healthy record.
 */
const WORK_TYPE_CLASSES: Readonly<Record<WorkType, string>> = {
  crf: 'bg-subtle text-fg ring-1 ring-line',
  project: 'bg-info-bg text-info-fg',
};

export function WorkTypeBadge({
  workType,
  only = 'project',
  className,
}: {
  workType: WorkType | null | undefined;
  /**
   * Draw the badge ONLY for this work type; `null` draws it for both. See the file
   * header — the default is the mixed-register answer, not a universal one.
   */
  only?: WorkType | null;
  className?: string;
}): React.JSX.Element | null {
  if (workType === null || workType === undefined) return null;
  if (only !== null && workType !== only) return null;
  return (
    <span
      data-work-type={workType}
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-pill px-2 py-0.5 text-label font-medium',
        WORK_TYPE_CLASSES[workType],
        className,
      )}
    >
      {WORK_TYPE_LABEL[workType]}
    </span>
  );
}
